import type { CheckoutAddress, PaymentMethod } from './commerce'
import { api } from './client'

function apiError(error: unknown, status: number, fallback: string): Error {
  if (error && typeof error === 'object' && 'error' in error) {
    const detail = (error as { error?: { message?: string } }).error?.message
    if (detail) return new Error(detail)
  }
  return new Error(`${fallback} (${status})`)
}

export async function getPlans(slug: string) {
  const { data, error, response } = await api.GET(
    '/api/v1/public/kitchens/{slug}/plans',
    { params: { path: { slug } } },
  )
  if (!data) throw apiError(error, response.status, 'Could not load plans')
  return data
}

export async function createSubscription(
  input: {
    planId: string
    startDate: string
    selectedWeekdays: number[]
    address: CheckoutAddress
    deliveryInstructions: string
    preferences: string[]
    exclusions: string[]
    paymentMethod: PaymentMethod
  },
  idempotencyKey: string,
) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions',
    {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body: {
        plan_id: input.planId,
        start_date: input.startDate,
        selected_weekdays: input.selectedWeekdays,
        address: input.address,
        delivery_instructions: input.deliveryInstructions,
        preferences: input.preferences,
        exclusions: input.exclusions,
        payment_method: input.paymentMethod,
      },
    },
  )
  if (!data) throw apiError(error, response.status, 'Could not start this plan')
  return data
}

export async function listSubscriptions() {
  const { data, error, response } = await api.GET(
    '/api/v1/customer/subscriptions',
  )
  if (!data) throw apiError(error, response.status, 'Could not load plans')
  return data
}

export async function transitionSubscription(
  subscriptionId: string,
  target: 'paused' | 'active' | 'cancelled',
) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions/{subscription_id}/transitions',
    {
      params: { path: { subscription_id: subscriptionId } },
      body: { target, reason: 'Customer requested in the PWA' },
    },
  )
  if (!data)
    throw apiError(error, response.status, 'Could not update this plan')
  return data
}

export async function skipFulfilment(fulfilmentId: string) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions/fulfilments/{fulfilment_id}/skip',
    { params: { path: { fulfilment_id: fulfilmentId } } },
  )
  if (!data)
    throw apiError(error, response.status, 'Could not skip this delivery')
  return data
}

export async function requestTravelHold(
  subscriptionId: string,
  input: { awayFrom: string; backOn: string; reason: string },
  idempotencyKey: string,
) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions/{subscription_id}/travel-holds',
    {
      params: {
        path: { subscription_id: subscriptionId },
        header: { 'Idempotency-Key': idempotencyKey },
      },
      body: {
        away_from: input.awayFrom,
        back_on: input.backOn,
        reason: input.reason,
      },
    },
  )
  if (!data)
    throw apiError(error, response.status, 'Could not request a travel hold')
  return data
}

export async function cancelTravelHold(subscriptionId: string, holdId: string) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions/{subscription_id}/travel-holds/{hold_id}/cancel',
    {
      params: {
        path: { subscription_id: subscriptionId, hold_id: holdId },
      },
    },
  )
  if (!data)
    throw apiError(error, response.status, 'Could not cancel this travel hold')
  return data
}

export async function renewSubscription(
  subscriptionId: string,
  paymentMethod: PaymentMethod,
  idempotencyKey: string,
) {
  const { data, error, response } = await api.POST(
    '/api/v1/customer/subscriptions/{subscription_id}/renewals',
    {
      params: {
        path: { subscription_id: subscriptionId },
        header: { 'Idempotency-Key': idempotencyKey },
      },
      body: { payment_method: paymentMethod },
    },
  )
  if (!data) throw apiError(error, response.status, 'Could not renew this plan')
  return data
}
